// Notifications (U2) — legacy utilities/NotificationsComponent.vue.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Snackbar,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { Notification, TickCircle } from 'iconsax-reactjs';

import { apiErrorMessage } from '../../api/client';
import { LoadingBlock } from '../../components/StateBlock';
import { formatDateTime, t } from '../../i18n';
import { AppNotification, Paginator, fetchNotifications, notificationActions } from './api';
import { REFRESH_ALERTS_EVENT } from './header/AlertsBell';
import { UI } from '../../brand/ui';

export function NotificationsPage() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);
  const unreadOnly = params.get('unread') === '1';
  const [data, setData] = useState<Paginator<AppNotification> | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchNotifications(page, unreadOnly));
    } catch (error) {
      setToast({ msg: apiErrorMessage(error, t('common.unableToLoad')), sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, unreadOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const run = async (action: () => Promise<unknown>, successKey: string) => {
    setBusy(true);
    try {
      await action();
      setToast({ msg: t(successKey), sev: 'success' });
      window.dispatchEvent(new Event(REFRESH_ALERTS_EVENT));
      await load();
    } catch (error) {
      setToast({ msg: apiErrorMessage(error, t('common.actionFailed')), sev: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const rows = data?.data ?? [];

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Notification size={28} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('nav.notifications')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <FormControlLabel
            control={<Switch checked={unreadOnly} onChange={(e) => setParam('unread', e.target.checked ? '1' : null)} />}
            label={t('shell.notifications.unreadOnly')}
          />
          <Button variant="outlined" disabled={busy || !rows.length} onClick={() => void run(notificationActions.readAll, 'shell.notifications.allRead')}>
            {t('shell.notifications.markAllRead')}
          </Button>
          <Button color="error" variant="outlined" disabled={busy || !rows.length} onClick={() => setConfirmAll(true)}>
            {t('shell.notifications.deleteAll')}
          </Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          {loading && !data ? (
            <LoadingBlock />
          ) : rows.length ? (
            <List disablePadding data-testid="notifications-list">
              {rows.map((n) => (
                <ListItem
                  key={n.id}
                  divider
                  sx={{ alignItems: 'flex-start', bgcolor: n.read_at ? 'transparent' : UI.accentAlpha(0.04), gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                  secondaryAction={undefined}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontWeight: n.read_at ? 600 : 700 }}>{n.data?.title ?? n.type.split('\\').pop()}</Typography>
                        {n.read_at ? <TickCircle size={16} color={UI.success} /> : <Chip size="small" color="primary" label={t('shell.notifications.new')} sx={{ height: 20 }} />}
                      </Stack>
                    }
                    secondary={
                      <>
                        {n.data?.description ? <Box component="span" sx={{ display: 'block' }}>{String(n.data.description)}</Box> : null}
                        <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: 12 }}>
                          {formatDateTime(n.created_at)}
                        </Box>
                      </>
                    }
                  />
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    {n.read_at ? (
                      <Button size="small" disabled={busy} onClick={() => void run(() => notificationActions.markUnread(n.id), 'shell.notifications.markedUnread')}>
                        {t('shell.notifications.markUnread')}
                      </Button>
                    ) : (
                      <Button size="small" disabled={busy} onClick={() => void run(() => notificationActions.markRead(n.id), 'shell.notifications.markedRead')}>
                        {t('shell.notifications.markRead')}
                      </Button>
                    )}
                    <Button size="small" color="error" disabled={busy} onClick={() => void run(() => notificationActions.remove(n.id), 'common.deleted')}>
                      {t('action.delete')}
                    </Button>
                  </Stack>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info">{t('shell.notifications.empty')}</Alert>
          )}
          {data && data.total > 0 ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('shell.tableInfo', { start: data.from ?? 0, end: data.to ?? 0, total: data.total })}
              </Typography>
              {data.last_page > 1 ? (
                <Pagination page={data.current_page} count={data.last_page} onChange={(_, value) => setParam('page', String(value))} color="primary" />
              ) : null}
            </Stack>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={confirmAll} onClose={() => setConfirmAll(false)}>
        <DialogTitle>{t('shell.notifications.deleteAll')}</DialogTitle>
        <DialogContent>
          <Typography>{t('shell.notifications.deleteAllConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAll(false)}>{t('action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => {
              setConfirmAll(false);
              void run(notificationActions.deleteAll, 'shell.notifications.allDeleted');
            }}
          >
            {t('action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}
